/** Inline CSS for the webphone widget — zero external dependencies */
export const WIDGET_CSS = /* css */ `
  .gw-webphone * { margin: 0; padding: 0; box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

  /* All widget UI elements re-enable pointer-events (host is pointer-events: none) */
  .gw-webphone > * { pointer-events: auto; }

  /* ── Theme vars ── */
  .gw-webphone { --bg: #0f1820; --bg2: #16232d; --bg3: #1c2f3a; --border: rgba(255,255,255,.08); --text: #e8edef; --text2: #7c8f9e; --text3: #4a5d6b; --green: #00a884; --green-h: #00c49a; --red: #ef4444; --red-h: #f87171; }
  .gw-webphone.gw-light { --bg: #ffffff; --bg2: #f0f2f5; --bg3: #e9edef; --border: rgba(0,0,0,.1); --text: #111b21; --text2: #667781; --text3: #8696a0; }

  /* ── FAB (Floating Action Button) ── */
  .gw-fab {
    position: fixed; z-index: 99999;
    width: 56px; height: 56px; border-radius: 50%;
    background: var(--green); border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 16px rgba(0,0,0,.3);
    transition: transform .15s, background .15s;
  }
  .gw-fab:hover { background: var(--green-h); transform: scale(1.05); }
  .gw-fab svg { width: 24px; height: 24px; fill: #fff; flex-shrink: 0; }
  .gw-fab .gw-badge { position: absolute; top: -2px; right: -2px; width: 18px; height: 18px; border-radius: 50%; background: var(--red); color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; }

  /* FAB ringing state */
  .gw-fab-ring { animation: gw-ring 1s ease-in-out infinite; }
  @keyframes gw-ring {
    0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0,168,132,.7); }
    50% { transform: scale(1.1); box-shadow: 0 0 0 18px rgba(0,168,132,0); }
  }

  /* FAB active call state */
  .gw-fab-active { background: var(--red); }
  .gw-fab-active:hover { background: var(--red-h); }

  /* ── Panel (Dialer) ── */
  .gw-panel {
    position: fixed; z-index: 99998;
    width: 360px; max-height: 520px;
    background: var(--bg); border: 1px solid var(--border);
    border-radius: 16px; overflow: hidden;
    box-shadow: 0 8px 40px rgba(0,0,0,.35);
    display: flex; flex-direction: column;
    transition: opacity .2s, transform .2s;
    opacity: 0; transform: translateY(10px) scale(.96); pointer-events: none;
  }
  .gw-panel.gw-open { opacity: 1; transform: translateY(0) scale(1); pointer-events: all; }

  /* ── Header ── */
  .gw-header {
    padding: 14px 18px; display: flex; align-items: center; justify-content: space-between;
    border-bottom: 1px solid var(--border); background: var(--bg2);
  }
  .gw-header-title { font-size: 14px; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
  .gw-header-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--text3); }
  .gw-header-dot.gw-on { background: var(--green); box-shadow: 0 0 6px rgba(0,168,132,.4); }
  .gw-header-close { background: none; border: none; cursor: pointer; color: var(--text2); font-size: 18px; padding: 4px; line-height: 1; }
  .gw-header-close:hover { color: var(--text); }

  /* ── Dialer ── */
  .gw-dialer { padding: 18px; }
  .gw-input-row { display: flex; gap: 8px; margin-bottom: 14px; }
  .gw-input {
    flex: 1; background: var(--bg2); border: 1px solid var(--border);
    border-radius: 8px; padding: 12px 14px; color: var(--text);
    font-size: 18px; letter-spacing: 1px; outline: none;
    font-family: 'Courier New', monospace;
  }
  .gw-input:focus { border-color: var(--green); }
  .gw-input::placeholder { color: var(--text3); font-size: 13px; letter-spacing: 0; }
  .gw-backspace {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 8px; padding: 0 14px; color: var(--text2);
    font-size: 18px; cursor: pointer;
  }
  .gw-backspace:hover { color: var(--text); background: var(--bg3); }

  .gw-dialpad { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin-bottom: 14px; }
  .gw-key {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 8px; padding: 12px; font-size: 18px; font-weight: 500;
    color: var(--text); cursor: pointer; text-align: center;
    transition: all .1s;
  }
  .gw-key:hover { background: var(--bg3); }
  .gw-key:active { background: var(--green); color: #000; transform: scale(.95); }

  .gw-call-btn {
    width: 100%; padding: 12px; border: none; border-radius: 10px;
    background: var(--green); color: #fff; font-size: 14px; font-weight: 700;
    cursor: pointer; transition: background .15s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    line-height: 1;
  }
  .gw-call-btn svg { width: 18px; height: 18px; flex-shrink: 0; }
  .gw-call-btn:hover { background: var(--green-h); }
  .gw-call-btn:disabled { background: var(--bg3); color: var(--text3); cursor: not-allowed; }

  /* ══════ Incoming Call Modal ══════ */
  .gw-incoming-modal {
    position: fixed; z-index: 99998;
    bottom: 86px; right: 20px; width: 300px;
    background: var(--bg); border: 1px solid var(--border);
    border-radius: 16px; padding: 24px;
    box-shadow: 0 8px 40px rgba(0,0,0,.5);
    text-align: center;
    opacity: 0; transform: translateY(20px); pointer-events: none;
    transition: opacity .3s, transform .3s;
  }
  .gw-incoming-modal.gw-show { opacity: 1; transform: translateY(0); pointer-events: all; }

  .gw-inc-pulse {
    width: 60px; height: 60px; border-radius: 50%;
    background: rgba(0,168,132,.15);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 12px;
    animation: gw-pulse 1.5s infinite;
  }
  @keyframes gw-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.15); opacity: .7; } }

  .gw-inc-label { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: var(--green); font-weight: 700; margin-bottom: 4px; }
  .gw-inc-phone { font-size: 20px; font-weight: 700; color: var(--text); font-family: 'Courier New', monospace; margin-bottom: 16px; }

  .gw-inc-actions { display: flex; gap: 16px; justify-content: center; }
  .gw-inc-accept {
    width: 56px; height: 56px; border-radius: 50%; border: none;
    background: var(--green); cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all .15s;
  }
  .gw-inc-accept:hover { background: var(--green-h); transform: scale(1.1); }
  .gw-inc-reject {
    width: 56px; height: 56px; border-radius: 50%; border: none;
    background: var(--red); cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all .15s;
  }
  .gw-inc-reject:hover { background: var(--red-h); transform: scale(1.1); }

  /* ══════ Active Call Panel ══════ */
  .gw-active-panel {
    position: fixed; z-index: 99998;
    bottom: 86px; right: 20px; width: 300px;
    background: var(--bg); border: 1px solid var(--border);
    border-radius: 16px; padding: 24px;
    box-shadow: 0 8px 40px rgba(0,0,0,.5);
    text-align: center;
    opacity: 0; transform: translateY(20px); pointer-events: none;
    transition: opacity .3s, transform .3s;
  }
  .gw-active-panel.gw-show { opacity: 1; transform: translateY(0); pointer-events: all; }

  .gw-act-status { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: var(--green); font-weight: 700; }
  .gw-act-phone { font-size: 18px; font-weight: 700; color: var(--text); font-family: 'Courier New', monospace; margin: 4px 0; }
  .gw-act-timer { font-size: 32px; font-weight: 300; color: var(--green); font-family: 'Courier New', monospace; margin: 8px 0; }

  .gw-act-level { height: 6px; background: var(--bg3); border-radius: 3px; overflow: hidden; margin: 8px 24px; }
  .gw-act-level-bar { height: 100%; width: 0%; background: var(--green); border-radius: 3px; transition: width 80ms; }

  .gw-act-actions { display: flex; gap: 16px; justify-content: center; margin-top: 12px; }
  .gw-act-btn {
    width: 48px; height: 48px; border-radius: 50%; border: none;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all .15s;
  }
  .gw-act-btn svg { width: 22px; height: 22px; }
  .gw-act-mute { background: var(--bg3); color: var(--text); }
  .gw-act-mute:hover { background: var(--bg2); }
  .gw-act-mute.gw-muted { background: var(--red); color: #fff; }
  .gw-act-mute svg { fill: currentColor; }
  .gw-act-hangup { background: var(--red); color: #fff; }
  .gw-act-hangup:hover { background: var(--red-h); }
  .gw-act-hangup svg { fill: #fff; }

  /* ── Spinner ── */
  .gw-spinner {
    width: 28px; height: 28px; margin: 0 auto;
    border: 3px solid var(--bg3); border-top-color: var(--green);
    border-radius: 50%; animation: gw-spin .8s linear infinite;
  }
  @keyframes gw-spin { to { transform: rotate(360deg); } }

  /* ── Outgoing-call (ringing) indicator ─────────────────────────────
   * Amber label + 3 pulsating dots so the user clearly distinguishes
   * "waiting for peer to pick up" from an active talk. When the peer
   * accepts, renderActive() flips to the timer + mic level bar. */
  .gw-act-status-ring { color: #f59e0b; }
  .gw-act-ring-dots {
    display: flex; justify-content: center; gap: 8px;
    height: 32px; align-items: center; margin: 8px 0;
  }
  .gw-act-ring-dots span {
    width: 10px; height: 10px; border-radius: 50%; background: #f59e0b;
    animation: gw-ring-dot 1.2s ease-in-out infinite;
  }
  .gw-act-ring-dots span:nth-child(2) { animation-delay: .2s; }
  .gw-act-ring-dots span:nth-child(3) { animation-delay: .4s; }
  @keyframes gw-ring-dot {
    0%, 60%, 100% { transform: scale(0.6); opacity: .4; }
    30%           { transform: scale(1);   opacity: 1;  }
  }

  /* ══════ "Atendida por X" transient banner (shown when call:taken or
     call:claim:denied arrives) — auto-dismisses after ~2s via the widget. */
  .gw-incoming-modal.gw-taken {
    background: rgba(0, 168, 132, .08);
    border-color: rgba(0, 168, 132, .4);
    padding: 20px 24px;
  }
  .gw-inc-taken {
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    animation: gw-taken-pop .35s ease-out;
  }
  .gw-inc-taken-check {
    width: 42px; height: 42px; border-radius: 50%;
    background: var(--green); color: #0a0f13;
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; font-weight: 800;
    animation: gw-taken-bounce .6s ease-out;
  }
  .gw-inc-taken-label {
    color: var(--green); font-weight: 700; font-size: 13px;
    text-transform: uppercase; letter-spacing: 1px; margin-top: 4px;
  }
  .gw-inc-taken-by {
    color: var(--text); font-size: 14px;
  }
  @keyframes gw-taken-pop {
    from { opacity: 0; transform: scale(.9); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes gw-taken-bounce {
    0%   { transform: scale(0); }
    60%  { transform: scale(1.2); }
    100% { transform: scale(1); }
  }

  /* ══════ Discreet mode (no FAB) — center modals on screen ══════ */
  .gw-incoming-modal.gw-discreet, .gw-active-panel.gw-discreet {
    bottom: auto; right: auto;
    top: 24px; left: 50%;
    transform: translateX(-50%) translateY(-10px);
  }
  .gw-incoming-modal.gw-discreet.gw-show, .gw-active-panel.gw-discreet.gw-show {
    transform: translateX(-50%) translateY(0);
  }

  /* ── Minimize button on modals ── */
  .gw-minimize-btn {
    position: absolute; top: 8px; right: 8px;
    width: 28px; height: 28px; border-radius: 50%; border: none;
    background: var(--bg3); color: var(--text2); cursor: pointer;
    font-size: 18px; line-height: 1; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    padding-bottom: 4px;
    transition: background .15s;
  }
  .gw-minimize-btn:hover { background: var(--bg2); color: var(--text); }

  /* ══════ Minimized pill (shows in place of modal when minimized) ══════ */
  .gw-minimized {
    position: fixed; z-index: 99997;
    top: 20px; right: 20px;
    min-width: 180px;
    background: var(--bg); border: 1px solid var(--border);
    border-radius: 999px;
    padding: 10px 18px;
    box-shadow: 0 4px 20px rgba(0,0,0,.4);
    color: var(--text); cursor: pointer;
    display: none;
    text-align: left;
    transition: transform .15s, box-shadow .15s;
  }
  .gw-minimized:hover { transform: scale(1.03); box-shadow: 0 6px 24px rgba(0,0,0,.5); }
  .gw-minimized.gw-show { display: block; }
  .gw-minimized.gw-min-ring { animation: gw-ring-pill 1.5s infinite; }
  @keyframes gw-ring-pill {
    0%,100% { box-shadow: 0 4px 20px rgba(0,168,132,.4); }
    50% { box-shadow: 0 4px 28px rgba(0,168,132,.8); }
  }
  .gw-min-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: var(--green); font-weight: 700; }
  .gw-min-phone { font-size: 13px; font-weight: 600; font-family: 'Courier New', monospace; }
  .gw-min-timer { font-size: 11px; color: var(--text2); font-family: 'Courier New', monospace; }

  /* ══════ Inline call-panel flash banner ══════
   * Slim transient banner that slides in from the top of the active call
   * panel (or incoming modal) to announce lifecycle events: "Chamada
   * atendida", "Recusada", "Encerrada", "Atendida por X". Auto-dismisses
   * after ~2.5s. Lives inside the panel so the user keeps focus on the
   * call context instead of looking at corner toasts. */
  .gw-call-flash {
    position: absolute; top: 0; left: 0; right: 0;
    display: flex; align-items: center; gap: 8px;
    padding: 8px 12px;
    font-size: 12px; font-weight: 600;
    color: #fff;
    background: var(--green);
    border-top-left-radius: 16px; border-top-right-radius: 16px;
    transform: translateY(-100%);
    opacity: 0;
    transition: transform .25s ease-out, opacity .25s ease-out;
    pointer-events: none;
    z-index: 2;
  }
  .gw-call-flash.gw-call-flash-show { transform: translateY(0); opacity: 1; }

  .gw-call-flash-success { background: var(--green); }
  .gw-call-flash-info    { background: #2563eb; }
  .gw-call-flash-warning { background: #f59e0b; color: #111; }
  .gw-call-flash-error   { background: var(--red); }

  .gw-call-flash-icon {
    flex-shrink: 0;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: rgba(255,255,255,.25);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 800;
  }
  .gw-call-flash-warning .gw-call-flash-icon { background: rgba(0,0,0,.18); }

  .gw-call-flash-text { flex: 1; min-width: 0; line-height: 1.3; }
  .gw-call-flash-text strong { font-weight: 700; }
  .gw-call-flash-text span { font-weight: 400; opacity: .9; }
`;
